import React, { useState, useEffect } from 'react';
import { ProductoService } from '../../services/productoService.js';
import { CategoriaService } from '../../services/categoriaService';
import ProductoForm from './ProductoForm';
import ProductoItem from './ProductoItem';
import ProductoOpcionesModal from './ProductoOpcionesModal';
import { MESSAGES } from '../../utils/constants';
import theme, { cn } from '../../styles/theme';

const ProductoList = ({ sucursalId, sucursalNombre, onBack }) => {
  const [producto, setProducto] = useState([]);
  const [categoria, setCategoria] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProducto, setEditingProducto] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  // ✅ NUEVO: estado modal opciones
  const [opcionesModal, setOpcionesModal] = useState(null);

  useEffect(() => {
    loadProducto();
    loadCategoria();
  }, []);

  const loadProducto = async () => {
    setLoading(true);
    try {
      const data = await ProductoService.getAllPr(sucursalId, true);
      setProducto(data);
    } catch (error) {
      showMessage('danger', MESSAGES.ERROR.GENERIC);
    }
    setLoading(false);
  };

  const loadCategoria = async () => {
    try {
      const data = await CategoriaService.getAllCa(sucursalId);
      setCategoria(data);
    } catch (error) {
      console.error('❌ Error cargando Categoria:', error);
    }
  };

  const testConnection = async () => {
    console.log('🧪 Iniciando test de conexión...');
    await ProductoService.testConnection();
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleCreate = () => { setEditingProducto(null); setShowForm(true); };

  const handleEdit = (producto) => { setEditingProducto(producto); setShowForm(true); };

  const handleFormSubmit = async (Data) => {
    setLoading(true);
    try {
      Data.Id_sucursal = sucursalId;
      if (editingProducto) {
        await ProductoService.update(editingProducto.Id_producto, Data);
        showMessage('success', MESSAGES.SUCCESS.UPDATE);
      } else {
        await ProductoService.create(Data);
        showMessage('success', MESSAGES.SUCCESS.CREATE);
      }
      setShowForm(false);
      setEditingProducto(null);
      await loadProducto();
    } catch (error) {
      showMessage('danger', MESSAGES.ERROR.GENERIC);
    }
    setLoading(false);
  };

  const handleDelete = (Id_producto) => { setRoleToDelete(Id_producto); setShowDeleteModal(true); };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await ProductoService.deleteRole(roleToDelete);
      showMessage('success', MESSAGES.SUCCESS.DELETE);
      await loadProducto();
    } catch (error) {
      showMessage('danger', MESSAGES.ERROR.GENERIC);
    }
    setShowDeleteModal(false);
    setRoleToDelete(null);
    setLoading(false);
  };

  const handleCancel = () => { setShowForm(false); setEditingProducto(null); };

  // ── COMPONENTES INTERNOS ────────────────────────────────────────────────────

  const MessageAlert = ({ message, onClose }) => {
    if (!message.text) return null;
    const styles = {
      success: 'bg-green-50 border-green-200 text-green-800',
      danger:  'bg-red-50 border-red-200 text-red-800',
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    };
    const icons = {
      success: <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>,
      danger:  <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>,
    };
    return (
      <div className={cn("border-l-4 p-4 mb-6 rounded-r-lg", styles[message.type] || 'bg-blue-50 border-blue-200 text-blue-800')}>
        <div className="flex items-start justify-between">
          <div className="flex items-start">
            <div className="mr-3 mt-0.5">{icons[message.type]}</div>
            <p className="font-medium">{message.text}</p>
          </div>
          <button onClick={onClose} className={cn("ml-4 text-gray-400 hover:text-gray-600", theme.transitions.default)}>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
          </button>
        </div>
      </div>
    );
  };

  const DeleteModal = ({ show, onHide, onConfirm, loading }) => {
    if (!show) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={cn(theme.classes.card.base, "max-w-md w-full p-6", theme.shadows['2xl'])}>
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-red-100 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Confirmar Eliminación</h3>
              <p className="text-sm text-gray-600 mt-1">Esta acción no se puede deshacer</p>
            </div>
          </div>
          <p className="text-gray-700 mb-6">¿Estás seguro de que deseas eliminar este producto? Todos los datos asociados se perderán permanentemente.</p>
          <div className="flex justify-end gap-3">
            <button onClick={onHide} disabled={loading} className={cn(theme.classes.button.secondary, "px-4 py-2 rounded-lg", theme.transitions.default, "disabled:opacity-50")}>Cancelar</button>
            <button onClick={onConfirm} disabled={loading} className={cn(theme.classes.button.danger, "px-6 py-2 rounded-lg", theme.transitions.default, "disabled:opacity-50 flex items-center gap-2")}>
              {loading ? (
                <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Eliminando...</>
              ) : 'Eliminar'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────

  if (showForm) {
    return (
      <div className={cn("min-h-screen py-8", theme.classes.background.main)}>
        <ProductoForm
          producto={editingProducto}
          onSubmit={handleFormSubmit}
          onCancel={handleCancel}
          isLoading={loading}
          categorias={categoria}
          sucursalId={sucursalId}
          sucursalNombre={sucursalNombre}
        />
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen p-6", theme.classes.background.main)}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className={cn(theme.classes.card.base, "p-6 mb-6")}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={cn(theme.classes.iconBox.primary, "p-3", theme.shadows.lg)}>
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4-8-4m16 0v10l-8 4-8-4V7" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestión de Productos</h1>
                {sucursalNombre && <p className="text-gray-600 mt-1">Sucursal: <span className="font-semibold text-gray-900">{sucursalNombre}</span></p>}
                <p className="text-sm text-gray-500">Administra los productos del sistema</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {onBack && (
                <button onClick={onBack} className={cn(theme.classes.button.secondary, "px-4 py-2 rounded-lg font-medium flex items-center gap-2", theme.transitions.default)}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                  Volver
                </button>
              )}
              <button onClick={testConnection} className={cn("px-4 py-2 text-gray-700 border-2 border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 font-medium flex items-center gap-2", theme.transitions.default)}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                Test API
              </button>
              <button onClick={handleCreate} className={cn(theme.classes.button.primary, "px-6 py-3 font-semibold rounded-lg transform hover:-translate-y-0.5 flex items-center gap-2", theme.transitions.default)}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                Crear Nuevo Producto
              </button>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: 'Total de Productos', value: producto.length, color: 'success' },
                { label: 'Productos Activos',  value: producto.filter(p => p.Estado === 'ACTIVO').length, color: 'info' },
                { label: 'Productos Inactivos',value: producto.filter(p => p.Estado === 'INACTIVO').length, color: 'warning' },
              ].map(s => (
                <div key={s.label} className="flex items-center gap-3">
                  <div className={theme.classes.iconBox[s.color]}>
                    <svg className={`w-5 h-5 text-${s.color === 'success' ? 'green' : s.color === 'info' ? 'blue' : 'orange'}-600`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{s.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <MessageAlert message={message} onClose={() => setMessage({ type: '', text: '' })} />

        {/* Tabla */}
        <div className={cn(theme.classes.card.base, "overflow-hidden")}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <svg className="animate-spin h-12 w-12 text-gray-900 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-lg font-medium text-gray-900">Cargando Productos...</p>
              </div>
            </div>
          ) : producto.length === 0 ? (
            <div className="text-center py-20">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4-8-4m16 0v10l-8 4-8-4V7" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay Productos disponibles</h3>
              <p className="text-gray-600 mb-6">Comienza creando tu primer producto en esta sucursal</p>
              <button onClick={handleCreate} className={cn(theme.classes.button.primary, "px-6 py-3 rounded-lg font-medium", theme.transitions.default)}>
                Crear Primer Producto
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['ID', 'Imagen', 'Producto', 'Descripcion', 'Precio', 'Categoria', 'Estado', 'Acciones'].map(h => (
                      <th key={h} className={`px-6 py-4 text-${h === 'Acciones' ? 'right' : 'left'} text-xs font-semibold text-gray-600 uppercase tracking-wider`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className={cn(theme.classes.background.card, "divide-y divide-gray-200")}>
                  {producto.map(p => (
                    <ProductoItem
                      key={p.Id_producto}
                      producto={p}
                      onEdit={handleEdit}
                      categoria={categoria}
                      onVerOpciones={(prod) => setOpcionesModal(prod)} // ✅ NUEVO
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DeleteModal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} onConfirm={confirmDelete} loading={loading} />

        {/* ✅ Modal opciones */}
        {opcionesModal && (
          <ProductoOpcionesModal
            producto={opcionesModal}
            onClose={() => setOpcionesModal(null)}
          />
        )}
      </div>
    </div>
  );
};

export default ProductoList;